import { Analytics } from '@cobliteam/analytics';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { useTranslation } from '@cobliteam/i18n';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Table from '@cobliteam/ui/components/Table';
import Typography from '@cobliteam/ui/components/Typography';
import {
  type ColumnDef,
  type ColumnPinningState,
  type ColumnSizingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Table as ITable,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import queryString from 'query-string';
import React, { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTrackVisibility } from 'react-intersection-observer-hook';
import invariant from 'tiny-invariant';
import { useIsLaptop } from '../AppSidebar/hooks/use-laptop';
import DataTableCell from './components/Cells/DataTableCell';
import DataTableActions from './components/DataTableActions';
import DataTableExport from './components/DataTableExport';
import DataTableHeader from './components/DataTableHeader';
import DataTablePagination from './components/DataTablePagination';
import type { ExportDef } from './components/Export';
import DataTableBody from './containers/DataTableBody';
import DataTableHead from './containers/DataTableHeader';
import TotalAndStats from './containers/DataTableStats';
import DataTableToolbar from './containers/DataTableToolbar';
import type { IFiltersDef, TFilterKeys } from './types';
import { augmentColumnsWithHeaderMinWidth, normalizeActionsColumn } from './utils';

/**
 * Props for the DataTable component.
 */
export type DataTableProps<TData, TValue> = {
  /**
   * The ID of the table.
   */
  id: string;
  /**
   * Determines whether the table should be virtualized.
   */
  isVirtualized?: boolean;

  /**
   * The ID of the scroll element.
   */
  scrollElementId?: string;

  /**
   * The estimated size of each row in pixels.
   */
  estimateSize?: number;

  /**
   * The definition of the filters in the table
   */
  filtersDef?: IFiltersDef[];

  /**
   * Determines whether the filters are loading.
   */
  filtersLoading?: boolean;

  /**
   * The options for the filters.
   * This is used to pass data to the filters.
   * For example, the `MultiSelectVehicles` filter requires the `vehicleGroups` and `vehicleTypes` options.
   */
  filtersOptions?: { key: TFilterKeys; options: any }[];

  /**
   * Determines whether the query params should be ignored.
   */
  ignoreQueryParams?: boolean;

  /**
   * The definition of the columns in the table.
   */
  columnsDef: ColumnDef<TData, TValue>[];

  /**
   * The initial sorting state of the table.
   */
  sortingDef?: SortingState;

  /**
   * The definition for exporting the table data.
   */
  exportDef?: ExportDef<TData>;

  /**
   * The data to be displayed in the table.
   */
  data: TData[] | undefined;

  /**
   * The total number of rows in the table.
   */
  totalRows?: number;

  /**
   * Whether to render the total number of rows or not.
   */
  showTotal?: boolean;

  /**
   * The custom date picker component.
   */
  datePicker?: React.ReactNode;

  /**
   * A custom filters component rendered right-aligned in the toolbar (e.g. FiltersDropdown).
   * Use this instead of filtersDef when managing filter state externally.
   */
  filtersDropdown?: React.ReactNode;

  /**
   * Determines whether the table is currently loading.
   */
  isLoading?: boolean;

  /**
   * Determines whether the table is currently fetching data.
   */
  isFetching?: boolean;

  /**
   * Determines how table rows are identified.
   */
  getRowId?: boolean;

  /**
   * The search query string.
   */
  search?: string | false;

  /**
   * Determines whether manual sorting is enabled.
   */
  manualSorting?: boolean;

  /**
   * Determines whether manual filtering is enabled.
   */
  manualFiltering?: boolean;

  /**
   * Determines whether manual pagination is enabled.
   */
  manualPagination?: boolean;

  /**
   * Determines if the table has the pagination component.
   */
  showPagination?: boolean;

  /**
   * The initial pagination state of the table.
   */
  initialPagination?: PaginationState;

  /**
   * Determines if the table has the column filter component.
   */
  showColumnFilter?: boolean;

  /**
   * The number of pages in the table.
   */
  pageCount?: number;

  /**
   * The placeholder text for the search input.
   */
  placeholder?: string;

  /**
   * The placeholder component to show when the table has no data or search result
   */
  emptyStatePlaceholder?: React.ReactNode;

  /**
   * Determines whether the row selection should be reset when the filter changes.
   */
  resetRowSelectionOnChangeFilter?: boolean;

  /**
   * Select actions
   * @deprecated use stickyActions instead
   */
  selectedActions?: (table: ITable<TData>, columnsDef: ColumnDef<TData, TValue>[]) => JSX.Element;

  /**
   * Sticky actions
   */
  stickyActions?: (table: ITable<TData>, columnsDef: ColumnDef<TData, TValue>[]) => JSX.Element;
  /**
   * The function for exporting the table data.
   */
  export?: (table: ITable<TData>, data: TData[], exportDef?: ExportDef<TData>) => JSX.Element;

  /**
   * The function for rendering the table statistics.
   */
  stats?: (table: ITable<TData>, data: TData[]) => JSX.Element | null;

  /**
   * The callback function for handling search query changes.
   */
  onChangeSearch?: (value: string) => void;

  /**
   * The callback function for handling sorting changes.
   */
  onChangeSort?: (sorts: SortingState) => void;

  /**
   * The callback function for handling filter changes.
   */
  onChangeFilter?: (filters: IFiltersDef) => void;

  /**
   * On select row callback
   */
  onChangeSelect?: (rows: any) => void;

  /**
   * On column visibility change callback
   */
  onChangeColumnVisibility?: (columns: VisibilityState) => void;

  /**
   * The callback function for refreshing the table data.
   */
  onRefresh?: () => void;

  /**
   * The callback function for handling pagination changes.
   */
  onChangePagination?: (pagination: PaginationState) => void;

  /**
   * The callback function for handling refetching the table data.
   */
  onRefetch?: () => void;

  /**
   * Determines whether the table should be animated.
   */
  live?: React.ReactNode;

  /**
   * Show TotalAndStats component.
   */
  showTotalAndStats?: boolean;

  /**
   * Render function for custom stats content below the toolbar. Receives the table instance and current data.
   */
  customStats?: (table: ITable<TData>, data: TData[]) => React.ReactNode;
};

const DataTable = <TData, TValue>({
  id,
  data,
  columnsDef,
  sortingDef,
  exportDef,
  filtersDef,
  filtersLoading = false,
  totalRows,
  showTotal = true,
  showTotalAndStats = true,
  datePicker,
  filtersDropdown,
  getRowId,
  isVirtualized = false,
  scrollElementId = 'app-body',
  estimateSize = 65,
  isLoading = false,
  isFetching = false,
  export: Export,
  stats: Stats,
  live,
  search = '',
  manualSorting = false,
  manualFiltering = false,
  manualPagination = false,
  showPagination = true,
  initialPagination,
  showColumnFilter = true,
  pageCount,
  placeholder,
  emptyStatePlaceholder,
  resetRowSelectionOnChangeFilter = false,
  filtersOptions,
  ignoreQueryParams = false,
  onRefresh,
  onChangeSearch,
  onChangeFilter,
  onChangeSort,
  onChangePagination,
  onChangeSelect,
  selectedActions,
  stickyActions,
  onChangeColumnVisibility,
  customStats,
}: DataTableProps<TData, TValue>): ReactElement => {
  invariant(id, 'ID is required for DataTable component');

  const { t } = useTranslation('ui-business');
  const defaultPlaceholder = placeholder || t('data_table.search_placeholder');

  const refBottomScroll = useRef<any>(null);
  const refContainerScroll = useRef<any>(null);

  const [headerSize, setHeaderSize] = useState(0);
  const [refTrackingTable, { isVisible: isVisibleTable }] = useTrackVisibility();
  const [refTrackingTableScroll, { isVisible: isVisibleTableScroll }] = useTrackVisibility();
  const [refTrackingStickyActions] = useTrackVisibility();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const lastSelectedRowRef = useRef<string | null>(null);
  const isShiftHeldRef = useRef(false);
  const tableRef = useRef<ITable<TData> | null>(null);
  const [sorting, setSorting] = useState<SortingState>(sortingDef || []);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({});
  const [localStorage, setLocalStorage] = useLocalStorage<any>('preferences');

  const prevSort = useRef(sorting);
  const prevSearch = useRef(globalFilter);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') isShiftHeldRef.current = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') isShiftHeldRef.current = false;
    };
    const onWindowBlur = () => {
      isShiftHeldRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  const handleRowSelectionChange = useCallback((updaterOrValue: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
    setRowSelection((prev) => {
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;

      const addedKeys = Object.keys(next).filter((k) => !prev[k]);
      const removedKeys = Object.keys(prev).filter((k) => !next[k]);
      const isSingleToggle = addedKeys.length + removedKeys.length === 1;
      const toggledId = addedKeys[0] ?? removedKeys[0];

      if (isSingleToggle && isShiftHeldRef.current && lastSelectedRowRef.current != null) {
        if (toggledId !== lastSelectedRowRef.current) {
          const rows = tableRef.current?.getRowModel().rows ?? [];
          const anchorIdx = rows.findIndex((r) => r.id === lastSelectedRowRef.current);
          const targetIdx = rows.findIndex((r) => r.id === toggledId);

          if (anchorIdx !== -1 && targetIdx !== -1) {
            const start = Math.min(anchorIdx, targetIdx);
            const end = Math.max(anchorIdx, targetIdx);
            const result = { ...prev };
            for (let i = start; i <= end; i++) {
              result[rows[i].id] = true;
            }
            return result;
          }
        }
      }

      if (isSingleToggle && !isShiftHeldRef.current && addedKeys.length === 1) {
        lastSelectedRowRef.current = addedKeys[0];
      }

      if (Object.keys(next).length === 0) {
        lastSelectedRowRef.current = null;
      }

      return next;
    });
  }, []);

  const [searchValue, setSearchValue] = useState<string>(search || '');
  const pageSizeStore: number = localStorage?.[`data-table-page-size-${id}`];

  const columnsStoreValue = localStorage?.[`data-table-columns-${id}`];
  const columnsStoreExists = columnsStoreValue !== undefined && columnsStoreValue !== null;
  const pageColumnsStore: [string] = JSON.parse(columnsStoreValue || '[]');

  const favoriteFiltersKeys: string[] = JSON.parse(localStorage?.[`data-table-filters-${id}`] || '[]');
  const favoriteFilters: TFilterKeys[] = filtersDef?.filter((filter) => favoriteFiltersKeys.includes(filter.key)).map((filter) => filter.key) || [];

  const params: any = queryString.parse(window.location.search, {
    arrayFormat: 'comma',
  });

  // only columns that have columnVisibility set to false
  const columnsObject: VisibilityState = {};

  if (params.columns) {
    const columnsList = Array.isArray(params.columns) ? params.columns : [params.columns];
    columnsList.map((column: string) => {
      columnsObject[column] = false;
    });
  } else if (columnsStoreExists) {
    pageColumnsStore.map((column: string) => {
      columnsObject[column] = false;
    });
  } else {
    columnsDef.map((column) => {
      if (column?.meta?.columnVisibility === false) {
        columnsObject[column.id as string] = column?.meta?.columnVisibility;
      }
    });
  }

  const pageIndex = params.page || 0;
  const pageSize = params.size || pageSizeStore || 10;

  const [columnVisibility, setColumnVisibility] = useState(columnsObject);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: Number.parseInt(pageIndex as string),
    pageSize: Number.parseInt(pageSize as string),
  });

  const rowsLoading = useMemo(() => Array(10).fill({}), []);

  const columnsLoading: any = useMemo(
    () =>
      columnsDef.map((column) => ({
        ...column,
        cell:
          column.id === 'actions'
            ? () => (
                <div className='flex items-center justify-center' style={{ height: estimateSize || 65 }}>
                  <Skeleton width={32} height={32} className='!bg-gray-3 !text-gray-3 !rounded-md' />
                </div>
              )
            : () => (
                <div className='justify-left flex items-center' style={{ height: estimateSize || 65 }}>
                  <Skeleton width='90%' height={24} className='!bg-gray-3 !text-gray-3'>
                    <svg width='80%' height='100%' className='!rounded-md'>
                      <rect x='0' y='0' width='100%' height='100%' />
                    </svg>
                  </Skeleton>
                </div>
              ),
      })),
    [columnsDef, estimateSize]
  );

  const tableColumns = useMemo(() => {
    const base = isLoading ? columnsLoading : columnsDef;
    return augmentColumnsWithHeaderMinWidth(normalizeActionsColumn(base));
  }, [isLoading, columnsLoading, columnsDef]);

  const tableData = isLoading ? rowsLoading : data;

  const table = useReactTable({
    initialState: {
      columnVisibility: columnsObject,
    },
    state: {
      rowSelection,
      sorting,
      globalFilter,
      columnVisibility,
      columnPinning,
      columnSizing,
      pagination,
    },
    manualFiltering,
    manualPagination,
    manualSorting,
    data: tableData || [],
    autoResetAll: false,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    enableRowSelection: true,
    pageCount,
    getRowId: getRowId ? (row) => row.id : undefined,
    onColumnPinningChange: (updater: any) => {
      // keep only the last column pinned
      setColumnPinning((prevState) => {
        const newState = updater(prevState);
        return {
          left: [newState.left[newState.left.length - 1]],
          right: newState.right,
        };
      });
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    tableRef.current = table;
  });

  const { rows } = table.getRowModel();
  const columns = table.getAllColumns();
  const visibleColumns = table.getVisibleFlatColumns();

  const virtualizer = useVirtualizer({
    enabled: isVirtualized && rows.length > 0,
    count: rows.length,
    overscan: 25,
    estimateSize: () => estimateSize,
    measureElement: (el) => el.getBoundingClientRect().height,
    getScrollElement: () => document.getElementById(scrollElementId),
  });

  const heightVirtualized = virtualizer.getTotalSize() === 0 ? '100%' : virtualizer.getTotalSize() + headerSize + 20; // + 20 for scrollbar placement
  const heightTable = isVirtualized ? heightVirtualized : '100%';
  const firstStart = virtualizer ? virtualizer.getVirtualItems()[0]?.start || 0 : 0;

  const memoOnChangeFilters = useMemo(() => {
    const onChangeFn = (item: IFiltersDef) => {
      if (item?.value?.length !== 0) {
        Analytics.track(`DataTable Filter Changed - ${item.key}`, { filter: item }, true);
      }

      if (resetRowSelectionOnChangeFilter) table.setRowSelection({});

      onChangeFilter?.(item);

      // reset pagination when filter changes
      const pagination = { pageIndex: 0, pageSize: Number(pageSize) };
      setPagination(pagination);
      onChangePagination?.(pagination);
    };

    return onChangeFn;
  }, [onChangeFilter, resetRowSelectionOnChangeFilter]);

  const onSelectedFiltersChange = useCallback(
    (keys: TFilterKeys[]) => {
      const filtersSet = new Set(keys);

      setLocalStorage({
        ...localStorage,
        [`data-table-filters-${id}`]: JSON.stringify(Array.from(filtersSet)),
      });
    },
    [localStorage, setLocalStorage, id]
  );

  const handleChangeFilter = useCallback(
    (value: IFiltersDef) => {
      table.resetPagination();
      memoOnChangeFilters(value);
    },
    [memoOnChangeFilters, table]
  );

  const handleChangeSearch = useCallback(
    (value: string) => {
      setSearchValue(value || '');
      setGlobalFilter(value || '');

      // reset pagination when search changes
      const pagination = { pageIndex: 0, pageSize: Number(pageSize) };
      setPagination(pagination);
      onChangePagination?.(pagination);
    },
    [onChangePagination, pageSize]
  );

  /**
   * Calls the `onChangeSort` callback with the `sorting` state.
   */
  useEffect(() => {
    if (onChangeSort) {
      if (prevSort.current.length !== sorting.length) {
        Analytics.track('DataTable Sort Changed', { sorting });
        prevSort.current = sorting;
      }

      onChangeSort(sorting);
    }
  }, [sorting]);

  /**
   * Applies the pagination value to the table.
   * This effect runs whenever the `data` or `totalRows` dependencies change.
   */
  useEffect(() => {
    const parsed = queryString.parse(window.location.search, {
      arrayFormat: 'comma',
    });
    const params = queryString.stringify(
      {
        ...parsed,
        page: pagination.pageIndex,
        size: pagination.pageSize,
      },
      { arrayFormat: 'comma' }
    );

    if (!ignoreQueryParams) {
      setLocalStorage({
        ...localStorage,
        [`data-table-page-size-${id}`]: pagination.pageSize,
      });

      window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
    }

    if (onChangePagination) onChangePagination(pagination);
  }, [pagination.pageIndex, pagination.pageSize]);

  /**
   * Calls the `onChangeSelect` callback with the `rowSelection` state.
   */
  useEffect(() => {
    if (onChangeSelect) onChangeSelect(table.getFilteredSelectedRowModel().rows.map((row) => row.original));
  }, [onChangeSelect, rowSelection, table]);

  /**
   * Float Scroll
   * Sets the width of the top scroll element to match the table width.
   * This effect runs whenever the `refTable`, `refTopScroll`, `refContainerScroll` or `columnVisibility` dependencies change.
   * */
  useEffect(() => {
    if (refBottomScroll?.current && refContainerScroll?.current) {
      const containerElement = refContainerScroll.current;
      const bottomScroll = refBottomScroll.current;
      const tableWidth = containerElement.scrollWidth;
      const contentElement = document.getElementById('dataTable');
      if (contentElement) {
        bottomScroll.children[0].style.width = `${tableWidth}px`;
        bottomScroll.style.width = `${contentElement.offsetWidth}px`;
      }

      // biome-ignore lint/suspicious/noAssignInExpressions: necessary for scroll synchronization
      const scrollTable = () => (bottomScroll.scrollLeft = containerElement.scrollLeft);
      // biome-ignore lint/suspicious/noAssignInExpressions: necessary for scroll synchronization
      const scrollTableBottom = () => (containerElement.scrollLeft = bottomScroll.scrollLeft);
      bottomScroll.addEventListener('scroll', scrollTableBottom);
      containerElement.addEventListener('scroll', scrollTable);

      return () => {
        bottomScroll.removeEventListener('scroll', scrollTable);
        containerElement.removeEventListener('scroll', scrollTableBottom);
      };
    }
  }, [refBottomScroll?.current, refContainerScroll?.current, JSON.stringify(columnVisibility)]);

  /**
   * Set initial column pinning based on column configuration
   */
  useEffect(() => {
    const initialPinnedColumns = columnsDef.filter((col) => col.enablePinning && !col.id?.includes('actions'));

    if (initialPinnedColumns.length > 0) {
      setColumnPinning((prev) => ({
        ...prev,
        left: [...(prev.left || []), ...initialPinnedColumns.map((col) => col.id as string).filter((id) => !prev.left?.includes(id))],
      }));
    }
  }, [columnsDef]);

  /**
   * Always pin the actions column to the right so it stays fixed regardless of resolution.
   */
  useEffect(() => {
    const hasActionsColumn = columnsDef.some((col) => col.id === 'actions');
    if (!hasActionsColumn) return;

    setColumnPinning((prev) => {
      const right = prev.right ? [...prev.right] : [];
      if (!right.includes('actions')) right.push('actions');
      return { left: prev.left, right };
    });
  }, [columnsDef]);

  /**
   * Save the column visibility state in the local storage
   */
  useEffect(() => {
    onChangeColumnVisibility?.(columnVisibility);
    const parsed = queryString.parse(window.location.search, {
      arrayFormat: 'comma',
    });

    const columns = Object.keys(columnVisibility).filter((column) => columnVisibility[column] === false);

    const params = queryString.stringify(
      {
        ...parsed,
        columns,
      },
      { arrayFormat: 'comma' }
    );

    setLocalStorage({
      ...localStorage,
      [`data-table-columns-${id}`]: JSON.stringify(columns),
    });

    window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
  }, [JSON.stringify(columnVisibility)]);

  /**
   * Reset pagination when initialPagination changes
   */
  useEffect(() => {
    if (initialPagination) setPagination(initialPagination);
  }, []);

  /**
   * Calls the `onChangeSearch` callback with the `search` state.
   */
  useEffect(() => {
    if (prevSearch.current !== globalFilter) {
      Analytics.track('DataTable Search Changed', { search: globalFilter });
      prevSearch.current = globalFilter;
      handleChangeSearch(globalFilter as string);
      onChangeSearch?.(globalFilter as string);
    }
  }, [handleChangeSearch, onChangeSearch, globalFilter]);

  const tableStyle = {
    height: heightTable,
    '--first-start': `${firstStart}px`,
  };

  const isBelowLaptop = useIsLaptop();
  const hasFilters = !!filtersDef && filtersDef.length > 0;
  const hasManyFilters = hasFilters && filtersDef.length > 3;
  // If not on mobile/tablet AND we have few filters, try to fit everything on a single line.
  const filtersOnSecondRow = isBelowLaptop || hasManyFilters;

  return (
    <>
      <section id='dataTable' data-testid='datatable-component' data-loading={isLoading || isFetching} className='flex flex-col gap-4'>
        <DataTableToolbar
          filtersOnSecondRow={filtersOnSecondRow}
          search={search}
          searchValue={searchValue || ''}
          defaultPlaceholder={defaultPlaceholder}
          handleChangeSearch={handleChangeSearch}
          onRefresh={onRefresh}
          isFetching={isFetching}
          isLoading={isLoading}
          datePicker={datePicker}
          filtersDropdown={filtersDropdown}
          showColumnFilter={showColumnFilter}
          columns={columns}
          visibleColumns={visibleColumns}
          exportDef={exportDef}
          Export={Export}
          data={data}
          table={table}
          filtersDef={filtersDef}
          live={live}
          filtersOptions={filtersOptions}
          filtersLoading={filtersLoading}
          onChangeFilter={handleChangeFilter}
          onSelectedFiltersChange={onSelectedFiltersChange}
          favoriteFilters={favoriteFilters}
        />

        {/* Stats and Info Bar */}
        {customStats ? customStats(table, data ?? []) : null}
        {showTotalAndStats && (
          <TotalAndStats
            onRefresh={onRefresh}
            isFetching={isFetching}
            isLoading={isLoading}
            totalRows={totalRows || table.getFilteredRowModel().rows?.length}
            showTotal={showTotal}
            table={table}
            data={data ?? []}
            stats={Stats}
          />
        )}

        {/* Data Table */}
        <div ref={refTrackingTable} className='relative'>
          <div ref={refContainerScroll} className='relative w-full overflow-x-auto overflow-y-hidden rounded-lg border border-gray-6 bg-white' style={tableStyle}>
            <Table style={{ width: table.getTotalSize(), minWidth: '100%', tableLayout: 'fixed' }}>
              <DataTableHead getHeaderSize={setHeaderSize} headers={table.getHeaderGroups()} size={table.getCenterTotalSize()} />
              <DataTableBody<TData, TValue>
                rows={rows}
                table={table}
                data={data || []}
                selectedActions={selectedActions}
                columnsDef={columnsDef}
                isLoading={isLoading}
                isFetching={isFetching}
                isVirtualized={isVirtualized}
                virtualizer={virtualizer}
                emptyStatePlaceholder={emptyStatePlaceholder}
              />
            </Table>
            <div ref={refTrackingStickyActions} />
            <div ref={refTrackingTableScroll} />
          </div>
          <div ref={refTrackingTableScroll} />
        </div>

        {/* Pagination */}
        {showPagination && !isVirtualized && data?.length ? (
          <div className='-mt-4 flex items-center justify-between rounded-lg bg-white'>
            <Typography color='secondary' variant='text-md-medium' data-testid='data-table-total'>
              Total: {isLoading ? <Skeleton className='inline-block h-2.5 w-4 bg-gray-3' /> : totalRows || table.getFilteredRowModel().rows?.length}
            </Typography>
            <DataTablePagination table={table} isFetching={isFetching} />
          </div>
        ) : null}
      </section>
      {!isVisibleTableScroll && isVisibleTable ? (
        <div ref={refBottomScroll} className='fixed bottom-0 z-20 h-6 overflow-x-auto'>
          <div className='h-px' />
        </div>
      ) : null}
      {stickyActions && Object.keys(rowSelection).length > 0 ? <div className='-translate-x-1/2 fixed bottom-8 left-1/2 z-30'>{stickyActions(table, columnsDef)}</div> : null}
    </>
  );
};

DataTable.Cell = DataTableCell;
DataTable.Header = DataTableHeader;
DataTable.Actions = DataTableActions;
DataTable.Export = DataTableExport;

export type { SortingState, VisibilityState };

export default DataTable;
